import React, { useState, useEffect, useCallback, useRef } from 'react';
import { S } from '../../styles';
import { API, authHeaders } from '../../utils/helpers';

// ── Settings › Website › Photos (1 Nov) ──────────────────────────────────────
//
// The photo library: every photo uploaded for the shop's website, bike photos
// included. Upload here once; the page builder picks from here. Each photo is
// shrunk once to at most 2400px (big enough for a full-width banner) and
// stored privately (server/routes/website.js, /admin/media).
//
// Also the site icon (favicon): one image becomes every size browsers and
// phones ask for, and applies straight away.

async function api(path, opts = {}) {
  const res = await fetch(`${API}/api/website${path}`, {
    credentials: 'include', ...opts,
    headers: { ...authHeaders(!!opts.body && !(opts.body instanceof FormData)), ...(opts.headers || {}) },
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || d.message || `Spinboard answered ${res.status}.`);
  return d;
}

const kb = (n) => (n ? (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`) : '');

// A photo narrower than this is stretched, and so looks soft, when a column,
// banner or slide shows it across a laptop screen (2 Nov: Manny's Road &
// Gravel and Electric photos looked blurry). It is a warning, not a refusal.
const SHARP_WIDTH = 1200;

export default function WebsitePhotos({ T, isDark, btn, sub }) {
  const [media, setMedia] = useState(null);
  const [site, setSite] = useState(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(0);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [query, setQuery] = useState('');
  const [source, setSource] = useState('all');
  const [rowError, setRowError] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [m, s] = await Promise.all([api('/admin/media'), api('/admin/site')]);
      setMedia(m.media || []); setSite(s); setError('');
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const tile = isDark ? '#1e2a40' : '#f4f4ef';
  const input = { background: T.input, border: `1px solid ${T.inputBorder}`, borderRadius: 6, padding: '5px 8px', color: T.text, fontSize: 12.5, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', width: '100%' };

  // One at a time, so a slow phone connection shows steady progress and one
  // bad file does not stop the rest.
  const upload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploadErrors([]);
    for (const file of list) {
      setUploading(n => n + 1);
      try {
        const fd = new FormData(); fd.append('file', file);
        const d = await api('/admin/media', { method: 'POST', body: fd });
        setMedia(m => [d.media, ...(m || [])]);
      } catch (e) { setUploadErrors(x => [...x, `${file.name}: ${e.message}`]); }
      setUploading(n => n - 1);
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const saveText = async (m, patch) => {
    const next = { ...m, ...patch };
    setMedia(list => list.map(x => (x.id === m.id ? next : x)));
    try { await api(`/admin/media/${m.id}`, { method: 'PUT', body: JSON.stringify({ name: next.name, alt: next.alt }) }); }
    catch (e) { setRowError({ id: m.id, text: e.message }); }
  };
  const remove = async (m) => {
    setRowError(null);
    try { await api(`/admin/media/${m.id}`, { method: 'DELETE' }); setMedia(list => list.filter(x => x.id !== m.id)); }
    catch (e) { setRowError({ id: m.id, text: e.message }); }
  };

  if (error && !media) return <div style={{ ...S.settingsCard, background: T.card, border: `1px solid ${T.cardBorder}`, color: T.text }}>Could not load photos: {error} <button style={btn(false)} onClick={load}>Try again</button></div>;
  if (!media) return <div style={{ color: T.textSub, padding: 20 }}>Loading photos…</div>;

  const q = query.trim().toLowerCase();
  const shown = media.filter(m => (source === 'all' || m.source === source) && (!q || `${m.name} ${m.alt}`.toLowerCase().includes(q)));
  const missingAlt = media.filter(m => !m.alt).length;

  return (
    <>
      <SiteIcon T={T} tile={tile} btn={btn} sub={sub} site={site} onChange={load} />

      <div style={{ ...S.settingsCard, background: T.card, border: `1px solid ${T.cardBorder}` }}>
        <div style={{ ...S.settingsCardTitle, color: T.text }}>Photo library</div>
        <div style={{ ...S.settingsCardDesc, color: T.textSub, marginBottom: 12 }}>
          Upload photos once and use them anywhere on your website: banners, columns, slides. Phone photos are fine; Spinboard shrinks
          them so pages stay fast. Describe each one for Google and for people using screen readers.
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button style={btn(true)} disabled={uploading > 0} onClick={() => fileRef.current && fileRef.current.click()}>
            {uploading > 0 ? `Uploading ${uploading}…` : '+ Upload photos'}
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => upload(e.target.files)} />
          <span style={sub}>{media.length} photo{media.length === 1 ? '' : 's'}{missingAlt ? ` · ${missingAlt} without a description` : ''}</span>
          <span style={{ flex: 1 }} />
          <select value={source} onChange={e => setSource(e.target.value)} style={{ ...input, width: 'auto' }}>
            <option value="all">All photos</option>
            <option value="library">Uploaded here</option>
            <option value="bike">Bike photos</option>
          </select>
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Find a photo" style={{ ...input, width: 170 }} />
        </div>
        {uploadErrors.length > 0 && (
          <div style={{ fontSize: 12.5, color: '#e74c3c', marginTop: 8 }}>{uploadErrors.map((x, i) => <div key={i}>Not uploaded: {x}</div>)}</div>
        )}

        {!media.length ? (
          <div style={{ ...sub, marginTop: 16 }}>No photos yet. Upload your first ones above.</div>
        ) : !shown.length ? (
          <div style={{ ...sub, marginTop: 16 }}>No photo matches that.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginTop: 14 }}>
            {shown.map(m => (
              <div key={m.id} style={{ background: tile, borderRadius: 8, padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <a href={`${API}${m.src}`} target="_blank" rel="noopener noreferrer" title="Open full size"
                  style={{ height: 130, background: '#fff', borderRadius: 6, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img src={`${API}${m.src}`} alt={m.alt || ''} loading="lazy" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                </a>
                <input style={{ ...input, fontWeight: 600 }} defaultValue={m.name} placeholder="Name" maxLength={120}
                  onBlur={e => e.target.value !== m.name && saveText(m, { name: e.target.value })} />
                <input style={input} defaultValue={m.alt} placeholder="Describe it, e.g. Rider on a Pivot Trailcat" maxLength={300}
                  onBlur={e => e.target.value !== m.alt && saveText(m, { alt: e.target.value })} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ ...sub, fontSize: 11, flex: 1 }}>
                    {m.width && m.height ? `${m.width}×${m.height}` : ''}{m.bytes ? ` · ${kb(m.bytes)}` : ''}{m.source === 'bike' ? ' · bike photo' : ''}
                  </span>
                  <button style={{ ...btn(false), padding: '2px 8px', color: '#e74c3c' }} onClick={() => remove(m)}>Delete</button>
                </div>
                {m.width && m.width < SHARP_WIDTH && <div style={{ fontSize: 11.5, color: '#e67e22' }}>Small photo: looks blurry in a wide section or banner. Upload a bigger original (at least {SHARP_WIDTH} pixels wide) if you have one.</div>}
                {rowError?.id === m.id && <div style={{ fontSize: 12, color: '#e74c3c' }}>{rowError.text}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function SiteIcon({ T, tile, btn, sub, site, onChange }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const ref = useRef(null);
  const icon = site?.draft?.favicon;
  const src = (path) => (path ? `${API}${path.replace('/_sb/photo/', '/api/website/photo/')}` : null);
  const pick = async (file) => {
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const fd = new FormData(); fd.append('file', file);
      await api('/admin/favicon', { method: 'POST', body: fd });
      await onChange();
    } catch (e) { setErr(e.message); }
    setBusy(false);
    if (ref.current) ref.current.value = '';
  };
  const clear = async () => {
    setBusy(true); setErr('');
    try { await api('/admin/favicon', { method: 'DELETE' }); await onChange(); } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  return (
    <div style={{ ...S.settingsCard, background: T.card, border: `1px solid ${T.cardBorder}` }}>
      <div style={{ ...S.settingsCardTitle, color: T.text }}>Site icon</div>
      <div style={{ ...S.settingsCardDesc, color: T.textSub, marginBottom: 12 }}>
        The small picture in the browser tab and on a phone's home screen. A square logo or badge works best, at least 512×512;
        Spinboard makes every size from it. Takes effect right away.
      </div>
      {!site?.exists ? (
        <div style={sub}>Your website is not in Spinboard yet, so there is nowhere to put an icon.</div>
      ) : (
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: tile, borderRadius: '8px 8px 0 0', padding: '8px 14px', minWidth: 210 }}
            title="How it looks in a browser tab">
            {icon?.s32 ? <img src={src(icon.s32)} alt="" width={16} height={16} /> : <span style={{ width: 16, height: 16, borderRadius: 3, border: `1px dashed ${T.border}` }} />}
            <span style={{ fontSize: 12.5, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{site?.draft?.shop?.name || 'Your shop'}</span>
          </div>
          {icon?.s180 && <img src={src(icon.s180)} alt="" width={56} height={56} style={{ borderRadius: 12, background: '#fff' }} title="On a phone's home screen" />}
          <input ref={ref} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" style={{ display: 'none' }} onChange={e => pick(e.target.files && e.target.files[0])} />
          <button style={btn(!icon)} disabled={busy} onClick={() => ref.current && ref.current.click()}>{busy ? 'Working…' : icon ? 'Replace icon' : 'Upload icon'}</button>
          {icon && !busy && <button style={{ ...btn(false), border: 'none', textDecoration: 'underline' }} onClick={clear}>Remove</button>}
          {err && <span style={{ fontSize: 12.5, color: '#e74c3c' }}>{err}</span>}
        </div>
      )}
    </div>
  );
}
